import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Profile } from '../../models/profile.model';
import { UserModel } from '../../models/user.model';
import { ConfirmService } from '../../services/confirm.service';
import { ProfileService } from '../../services/profile.service';
import { UserService } from '../../services/user.service';

/**
 * Modal de edicion de usuario.
 *
 * El API solo expone PUT /users/{uid}: no hay alta ni baja de usuarios desde la
 * consola (se crean en la app movil). `email` y `uid` se muestran de solo
 * lectura porque el API no los actualiza.
 *
 * Sus campos son signals publicos porque ng-bootstrap solo expone
 * `componentInstance` (mismo patron que ConfirmDialogComponent).
 */
@Component({
  selector: 'app-user-modal',
  imports: [NgSelectComponent, ReactiveFormsModule],
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly profileService = inject(ProfileService);

  /** Usuario en edicion. Lo inyecta el llamador con `setUser()`. */
  readonly user = signal<UserModel | null>(null);

  protected readonly submitting = signal(false);

  /** Perfil que traia el usuario antes de abrir el modal. */
  private readonly originalProfile = signal<Profile | null>(null);

  protected readonly form = this.formBuilder.nonNullable.group({
    names: ['', [Validators.required, Validators.maxLength(120)]],
    lastNames: ['', [Validators.required, Validators.maxLength(120)]],
    profile: ['user' as Profile, [Validators.required]],
    status: [true],
  });

  /**
   * Lo que consume el `<ng-select>`. Se precalcula la etiqueta en vez de
   * componerla en la plantilla porque `bindLabel` apunta a UNA propiedad y esa
   * misma propiedad es sobre la que ng-select busca.
   */
  protected readonly profileOptions = computed(() =>
    this.profileService.profiles().map((item) => ({
      profile: item.profile,
      label: `${item.profile} — ${item.description}`,
    })),
  );

  /**
   * Atributos que ng-select copia al `<input role="combobox">` interno, que es
   * el elemento que recibe el foco y, por tanto, el unico al que el lector de
   * pantalla le lee `aria-describedby` y `aria-invalid`. El `id` NO va aqui:
   * lo pone `[labelForId]`, que es lo que enlaza el `<label for>`.
   */
  protected profileInputAttrs(): Record<string, string> {
    return {
      'aria-describedby': 'ks-user-profile-desc',
      'aria-invalid': this.invalid('profile') ? 'true' : 'false',
    };
  }

  /** Valor vivo del select de perfil, para pintar la advertencia de sudo. */
  protected readonly selectedProfile = signal<Profile>('user');

  protected readonly isSudoSelected = computed(() => this.selectedProfile() === 'sudo');

  /** Solo es una ESCALADA de privilegios si antes no era sudo. */
  protected readonly isSudoEscalation = computed(
    () => this.isSudoSelected() && this.originalProfile() !== 'sudo',
  );

  protected readonly initials = computed(() => {
    const user = this.user();
    if (!user) return '';
    const first = user.names.trim().charAt(0);
    const last = user.lastNames.trim().charAt(0);
    return `${first}${last}`.toUpperCase() || user.email.charAt(0).toUpperCase();
  });

  constructor() {
    void this.profileService.ensureProfiles();

    this.form.controls.profile.valueChanges.subscribe((profile) => {
      this.selectedProfile.set(profile);
    });
  }

  /** Carga el usuario a editar. Lo llama quien abre el modal. */
  setUser(user: UserModel): void {
    this.user.set(user);
    this.originalProfile.set(user.profile);
    this.selectedProfile.set(user.profile);
    this.form.reset({
      names: user.names,
      lastNames: user.lastNames,
      profile: user.profile,
      status: user.status,
    });
  }

  protected invalid(control: 'names' | 'lastNames' | 'profile'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || field.dirty);
  }

  protected async submit(): Promise<void> {
    const user = this.user();
    if (!user || this.submitting()) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { names, lastNames, profile, status } = this.form.getRawValue();

    // FRONTERA DE SEGURIDAD: sudo da acceso total a la consola administrativa.
    if (profile === 'sudo' && this.originalProfile() !== 'sudo') {
      const confirmed = await this.confirmService.ask({
        title: 'Otorgar perfil sudo',
        message:
          'El perfil sudo da acceso TOTAL a la consola administrativa: podrá ver y editar a todos los usuarios, cerrar sus sesiones y modificar los catálogos.',
        detail: `${user.fullName} · ${user.email}`,
        confirmLabel: 'Otorgar sudo',
        cancelLabel: 'Cancelar',
        variant: 'danger',
      });
      if (!confirmed) return;
    }

    this.submitting.set(true);
    try {
      const updated = await this.userService.updateUser(user.uid, {
        names: names.trim(),
        lastNames: lastNames.trim(),
        profile,
        status,
      });
      if (updated) this.activeModal.close(updated);
    } finally {
      this.submitting.set(false);
    }
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
